import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import {
  ArrowLeft, Loader2, Zap, CheckCircle2, Clock, AlertCircle,
  ChevronLeft, ChevronRight, ArrowDown, ArrowUp, Gift, CreditCard, RefreshCcw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

// Colour + icon per status. Keep in one place so a status added later
// (e.g. "Cancelled") only needs a single row here.
const STATUS_STYLES = {
  Completed:  { color: 'text-emerald-400', icon: CheckCircle2 },
  Processing: { color: 'text-[#FFB238]',   icon: Clock },
  Failed:     { color: 'text-red-400',     icon: AlertCircle },
};

// Icon overrides for credit-in events so they're visually distinct from
// the spend rows. Falls back to the status icon otherwise.
const SERVICE_ICONS = {
  'Top-up':       CreditCard,
  Subscription:   CreditCard,
  Renewal:        RefreshCcw,
  Gift:           Gift,
};

function formatRequestedAt(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return then.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Wall-clock generation time. "How long did I have to wait?"
function formatGenerationTime(secs) {
  if (secs == null) return '—';
  const s = Number(secs);
  if (!Number.isFinite(s) || s < 0) return '—';
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

// Compact one-liner shown UNDER the service name in the Service cell.
// Videos/sounds: "1080p · 8s"; images: "1440p"; credit-in rows: nothing.
function serviceSubtitle(r) {
  const parts = [];
  if (r.resolution) parts.push(r.resolution);
  if (r.media_length_seconds) parts.push(`${r.media_length_seconds}s`);
  return parts.join(' · ');
}

export default function AccountHistory() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPage = useCallback(async (targetPage) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/user/generation-history`, {
        params: { limit: PAGE_SIZE, skip: targetPage * PAGE_SIZE },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(res.data?.requests || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load generation history');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!user) { navigate('/account'); return; }
    fetchPage(page);
  }, [user, navigate, page, fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endRow = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-[#0F1419] text-white" data-testid="account-history-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button
          onClick={() => navigate('/account')}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6"
          data-testid="history-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </button>

        <h1 className="text-3xl font-bold mb-1">Generation History</h1>
        <p className="text-gray-400 mb-8">
          Every generation you&apos;ve made and exactly what it cost, plus every top-up, subscription renewal and gift you&apos;ve claimed. Refunded credits from failed generations are noted below.
        </p>

        {loading && rows.length === 0 && (
          <div className="flex items-center gap-2 text-gray-400 py-16 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading your history…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl p-6 bg-red-500/10 border border-red-500/30 text-red-300">
            {error}
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="rounded-xl p-10 bg-[#1A2035] border border-[#222D4A] text-center text-gray-400">
            You haven&apos;t generated anything yet.
          </div>
        )}

        {!error && rows.length > 0 && (
          <>
            <div className="rounded-2xl bg-[#1A2035] border border-[#222D4A] overflow-hidden">
              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full" data-testid="history-table">
                  <thead className="bg-[#222D4A] text-xs uppercase tracking-wide text-gray-400">
                    <tr>
                      <th className="text-left px-6 py-3 font-medium">Requested</th>
                      <th className="text-left px-6 py-3 font-medium">Service</th>
                      <th className="text-left px-6 py-3 font-medium">Status</th>
                      <th className="text-left px-6 py-3 font-medium" title="How long it took to render">Time</th>
                      <th className="text-right px-6 py-3 font-medium">Credits</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#222D4A]">
                    {rows.map((r) => {
                      const st = STATUS_STYLES[r.status] || STATUS_STYLES.Processing;
                      const StatusIcon = SERVICE_ICONS[r.service] || st.icon;
                      const isCredit = r.direction === 'credit';
                      const subtitle = serviceSubtitle(r);
                      return (
                        <tr key={r.id} className="hover:bg-[#222D4A]/40" data-testid={`history-row-${r.id}`}>
                          <td className="px-6 py-4 text-sm text-gray-300">{formatRequestedAt(r.requested_at)}</td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-white">{r.service}</div>
                            {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
                          </td>
                          <td className="px-6 py-4">
                            <div className={`inline-flex items-center gap-1.5 text-sm ${isCredit ? 'text-emerald-400' : st.color}`}>
                              <StatusIcon className="w-4 h-4" />
                              <span>{isCredit ? 'Added' : r.status}</span>
                            </div>
                            {r.refunded && (
                              <div className="text-xs text-emerald-400 mt-0.5" data-testid="history-refunded">Refunded</div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-300">{isCredit ? '—' : formatGenerationTime(r.generation_time_seconds)}</td>
                          <td className="px-6 py-4 text-right">
                            <div className={`inline-flex items-center gap-1.5 font-medium ${isCredit ? 'text-emerald-400' : 'text-white'}`}>
                              {isCredit ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUp className="w-3.5 h-3.5 text-gray-500" />}
                              <Zap className={`w-3.5 h-3.5 ${isCredit ? 'text-emerald-400' : 'text-[#FFB238]'}`} fill={isCredit ? '#34D399' : '#FFB238'} />
                              {isCredit ? '+' : ''}{r.credits}
                            </div>
                            {r.refunded && (
                              <div className="text-xs text-emerald-400 mt-0.5">+{r.credits} refunded</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile stacked cards */}
              <div className="md:hidden divide-y divide-[#222D4A]">
                {rows.map((r) => {
                  const st = STATUS_STYLES[r.status] || STATUS_STYLES.Processing;
                  const StatusIcon = SERVICE_ICONS[r.service] || st.icon;
                  const isCredit = r.direction === 'credit';
                  const subtitle = serviceSubtitle(r);
                  return (
                    <div key={r.id} className="p-4" data-testid={`history-card-${r.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-medium text-white">{r.service}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {formatRequestedAt(r.requested_at)}
                            {subtitle ? ` · ${subtitle}` : ''}
                            {!isCredit && r.generation_time_seconds != null ? ` · ${formatGenerationTime(r.generation_time_seconds)}` : ''}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`inline-flex items-center gap-1 font-medium ${isCredit ? 'text-emerald-400' : ''}`}>
                            <Zap className={`w-3.5 h-3.5 ${isCredit ? 'text-emerald-400' : 'text-[#FFB238]'}`} fill={isCredit ? '#34D399' : '#FFB238'} />
                            {isCredit ? '+' : ''}{r.credits}
                          </div>
                          {r.refunded && (
                            <div className="text-[10px] text-emerald-400">+{r.credits} refunded</div>
                          )}
                        </div>
                      </div>
                      <div className={`inline-flex items-center gap-1.5 text-xs mt-2 ${isCredit ? 'text-emerald-400' : st.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        <span>{isCredit ? 'Credits added' : r.status}{r.refunded ? ' — Refunded' : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 text-sm text-gray-400" data-testid="history-pagination">
              <div>
                Showing <span className="text-white font-medium">{startRow}</span>–<span className="text-white font-medium">{endRow}</span> of <span className="text-white font-medium">{total}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#222D4A] border border-[#2A3550] hover:border-[#FFB238]/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="history-prev-page"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <span className="px-2 text-gray-500">
                  Page <span className="text-white font-medium">{page + 1}</span> of <span className="text-white font-medium">{totalPages}</span>
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-[#222D4A] border border-[#2A3550] hover:border-[#FFB238]/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="history-next-page"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
